"use client";

import Image from "next/image";
import { Play } from "lucide-react";
import { useMemo, useState } from "react";
import NewsCard from "./news-card";
import { useHomepageNews } from "../hooks/useHomepageNews";
import { href } from "@/lib/localized-paths";
import { useLocale } from "@/app/[lang]/providers/LocaleProvider";
import { useDict } from "@/app/[lang]/providers/DictionaryProvider";
import { MenuId, SectionId } from "@/lib/enum";
import { HomePage } from "../type/type";

function getYoutubeVideoId(url?: string | null) {
  if (!url) return "E4TujWAOALM";

  try {
    const parsed = new URL(url);
    const watchId = parsed.searchParams.get("v");

    if (watchId) return watchId;
    if (parsed.hostname.includes("youtu.be")) return parsed.pathname.replace("/", "");
    if (parsed.pathname.includes("/embed/")) return parsed.pathname.split("/embed/")[1]?.split("/")[0] || null;

    return parsed.pathname.split("/").filter(Boolean).pop() || null;
  } catch {
    return url.split("/").filter(Boolean).pop() || null;
  }
}

export default function Insight({ data }: { data: HomePage | undefined }) {
  const locale = useLocale();
  const dict = useDict();
  const { data: newsData, isLoading, isError } = useHomepageNews();
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoId = useMemo(() => getYoutubeVideoId(data?.discover_youtube_link), [data?.discover_youtube_link]);
  const videoTitle = data?.discover_title || "Discover Our Insights";

            
  if (isLoading || isError) return null;
  return (
    <section className=" bg-gradient-to-b from-[rgba(237,237,249,0)] to-[#DEE5E8] ">
      <div className="bg-[url('/images/left-corner-supergrafis.png')] bg-no-repeat bg-left-bottom">
      <div className="max-w-7xl mx-auto px-4 md:px-8 ">
        {/* top section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center">
          {/* left text */}
          <div className="bg-[url('/images/right-corner-orange-supergrafis.svg')] bg-no-repeat bg-right-top">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0C2340] mt-8 md:mt-12">
              {data?.discover_title || dict.homepage?.discover_insights || "Discover Our Insights"}
            </h2>
            <div className="mt-4 text-gray-600 leading-relaxed max-w-lg" dangerouslySetInnerHTML={{ __html: data?.discover_desc || dict.homepage?.insight_intro || "Established on July 22, 2005, PT Sarana Multigriya Finansial (Persero) or SMF is a Special Mission Vehicle under the Ministry of Finance, tasked with promoting economic stability by providing long-term liquidity to financial institutions. SMF expands access to housing finance to help reduce the housing ownership and occupancy backlog in a sustainable and inclusive way" }} />
          </div>

          {/* right video */}
          <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-md">
            {isVideoLoaded && videoId ? (
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                title={videoTitle}
                className="w-full h-full"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : (
              <button
                type="button"
                aria-label={`Play video: ${videoTitle}`}
                className="group relative block h-full w-full bg-black"
                onClick={() => setIsVideoLoaded(true)}
              >
                {videoId && (
                  <Image
                    src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
                    alt=""
                    fill
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    className="object-cover opacity-90 transition-opacity group-hover:opacity-100"
                    unoptimized
                  />
                )}
                <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-primary-800 shadow-lg transition-transform group-hover:scale-105">
                    <Play className="ml-1 h-8 w-8 fill-current" aria-hidden="true" />
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>

        {/* bottom cards (plain div) */}
        <div id="card-section" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 my-12">
          {(() => {
            return newsData?.data.map((item) => (
              <NewsCard
                key={item.id}
                image={item.image}
                imageAlt={item.imageAlt || item.title}
                category={item.category}
                title={item.title}
                href={href(locale, MenuId.Publications, SectionId.NewsActivities) + `/${item.slug}`}
                readMore={dict.common?.readMore}
              />
            )) || [];
          })()}
        </div>
      </div>
    </div>
  </section>
  );
}
